import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import type { AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useMemo } from 'react';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const useProcessedFilters = (filterConditions: FilterConditionOutput[], sorting: SortingConfig | null) => {
  return useMemo(() => {
    // 🎯 Procesar filtros avanzados del usuario
    const userAdvancedFilters = {
      filters: [] as Array<{
        column: string;
        operator: string;
        value: string | string[] | number | number[];
      }>,
      logic: 'AND' as 'AND' | 'OR',
    };

    // 🎯 Campos numéricos que requieren conversión de string a number
    const numericColumns = [
      'product_id',
      'user_id',
      'event_id',
      'qty',
      'team_id',
      'order_id',
      'stock_location_order_id',
      'returned_reception_id',
      'entry_id',
      'extra_work_order_id',
      'location_order_id',
      'batch_id',
    ];

    // Mapear condiciones de filtro del usuario al formato de la API
    filterConditions.forEach((condition) => {
      const { column, action, value } = condition;

      // 🎯 Mapear nombres de columnas a los campos reales de la API
      // Los campos ya coinciden exactamente con los permitidos por la API
      const apiColumn = column;

      // 🎯 Mapear operadores de acción a operadores de la API
      // Los operadores ya coinciden exactamente con los permitidos por la API
      const apiOperator = action;

      // 🎯 Verificar si es un campo numérico
      const isNumericColumn = numericColumns.includes(apiColumn);

      // Procesar valores según el tipo de operador
      let processedValue: string | string[] | number | number[] = value;

      if (action === 'in' || action === 'not_in') {
        // Para operadores de lista, mantener como array
        const arrayValue = Array.isArray(value) ? value : [value];
        // Convertir a números si es un campo numérico
        processedValue = isNumericColumn ? arrayValue.map((v) => (typeof v === 'string' ? Number(v) : v)) : arrayValue;
      } else if (action === 'is_null' || action === 'is_not_null') {
        advancedFilters.filters.push({
          column: apiColumn,
          operator: apiOperator,
        });
        return;
      } else if (action === 'between' && Array.isArray(value) && value.length === 2) {
        // Para rangos de fechas, usar el formato correcto
        processedValue = `${value[0]},${value[1]}`;
      } else {
        // Para otros operadores (greater_than, less_than, equals, etc.), usar solo el primer valor
        // Esto evita el problema de "2025-08-08 to 2025-08-08"
        const singleValue = Array.isArray(value) ? value[0] : value;
        // Convertir a número si es un campo numérico
        processedValue = isNumericColumn && typeof singleValue === 'string' ? Number(singleValue) : singleValue;
      }

      userAdvancedFilters.filters.push({
        column: apiColumn,
        operator: apiOperator,
        value: processedValue,
      });
    });

    return {
      // 🎯 Solo incluir advanced_filters si el usuario ha agregado filtros
      ...(userAdvancedFilters.filters.length > 0
        ? {
            advanced_filters: userAdvancedFilters,
          }
        : {}),
      sortBy: sorting?.field ?? 'id',
      sortDirection: sorting?.direction ?? 'desc',
    };
  }, [filterConditions, sorting]);
};
